import React, { useEffect, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';

interface CaptureLivePreviewProps {
  stream: MediaStream | null;
  warning?: string | null;
  className?: string;
}

/** Shows the live getDisplayMedia feed so operators can confirm camera video is visible before recording. */
const CaptureLivePreview: React.FC<CaptureLivePreviewProps> = ({ stream, warning, className = '' }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.srcObject = stream;
    if (stream) {
      el.play().catch(() => undefined);
    }
    return () => {
      el.srcObject = null;
    };
  }, [stream]);

  if (!stream) {
    return (
      <div className={`flex flex-col items-center justify-center text-gray-400 ${className}`}>
        <div className="text-6xl mb-4">📹</div>
        <h3 className="text-xl font-medium mb-2 text-gray-300">Screen capture preview</h3>
        <p className="text-sm text-center max-w-md px-4">
          Click &quot;Start Recording&quot;, then choose the SmartPSS window or your full screen in the browser
          picker.
        </p>
      </div>
    );
  }

  return (
    <div className={`relative w-full h-full flex flex-col ${className}`}>
      {warning && (
        <div className="absolute top-3 left-3 right-3 z-10 flex gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 shadow-sm">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <p>{warning}</p>
        </div>
      )}
      <video
        ref={videoRef}
        muted
        playsInline
        autoPlay
        className="w-full h-full object-contain bg-black"
      />
    </div>
  );
};

export default CaptureLivePreview;
